import classNames from "classnames";
import React, { FC, FormEvent, useEffect, useState } from "react";
import { LuChevronUp, LuRefreshCw } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import {
  AiBriefing,
  AiBriefingItem,
  useGetAiBriefingQuery,
} from "../../../../slices/AiSlice";
import { openAskPanel } from "../../../ai/ask/askEvent";
import { useAiStatus } from "../../../ai/useAiStatus";
import { useClientEmailCompose } from "../../../contacts/clientEmail/useClientEmailCompose";
import {
  ASK_SUGGESTION,
  briefingHeading,
  busiestLine,
  countChips,
  isAppLink,
  readExpanded,
  saveExpanded,
  waitingAction,
  writtenLine,
} from "./dailyBriefing.utils";

const BAND =
  "rounded-[12px] border border-[#E6E0FF] bg-[#F6F3FF] text-[#1F2433]";
const PURPLE_BUTTON =
  "inline-flex shrink-0 items-center gap-1.5 rounded-[9px] bg-[#6B4EFF] px-3 py-1.5 text-[12.5px] font-bold text-white hover:bg-[#5A3FE6] disabled:opacity-60";
const LIGHT_BUTTON =
  "inline-flex shrink-0 items-center gap-1.5 rounded-[9px] border border-[#DCE2EA] bg-white px-2.5 py-1 text-[12px] font-semibold text-[#1F2433] hover:border-[#6B4EFF] hover:text-[#6B4EFF] disabled:opacity-60";

type ViewProps = {
  /** The AI-worded briefing for the card (only fetched once "Show my day" is clicked). */
  briefing?: AiBriefing;
  /** The plain lists (no AI call) for the collapsed line's counts; same counts as the card. */
  band?: AiBriefing;
  /** The card's AI wording is being written. */
  writing?: boolean;
  loading: boolean;
  failed: boolean;
  refreshing: boolean;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  onRefresh: () => void;
  onRetry: () => void;
  onOpen: (link: string) => void;
  onEmail: (item: AiBriefingItem) => void;
  onAsk: (question: string) => void;
  /** Home draws the two parts in different places (owner 2026-11-01): "band" = only the collapsed line (in the
   * greeting row), "card" = only the opened card (below it). Left out = both, in one place. */
  part?: "band" | "card";
};

const Chip: FC<{ text: string }> = ({ text }) => (
  <span
    className={
      "rounded-full border border-[#E6E0FF] bg-white px-2.5 py-0.5 text-[12px] font-semibold text-[#4B3BB8]"
    }
  >
    {text}
  </span>
);

const ItemTitle: FC<{
  item: AiBriefingItem;
  onOpen: (link: string) => void;
}> = ({ item, onOpen }) =>
  isAppLink(item.link) ? (
    <button
      type={"button"}
      onClick={() => onOpen(item.link)}
      className={
        "block max-w-full truncate text-left text-[13.5px] font-semibold text-[#6B4EFF] hover:underline"
      }
      title={item.title}
    >
      {item.title}
    </button>
  ) : (
    <span
      className={"block truncate text-[13.5px] font-semibold text-[#1F2433]"}
      title={item.title}
    >
      {item.title}
    </span>
  );

const Column: FC<{
  title: string;
  empty: string;
  items: AiBriefingItem[];
  onOpen: (link: string) => void;
  onEmail?: (item: AiBriefingItem) => void;
  footer?: string | null;
}> = ({ title, empty, items, onOpen, onEmail, footer }) => (
  <section
    aria-label={title}
    className={"min-w-0 rounded-[12px] border border-[#EDEFF3] bg-white p-3.5"}
  >
    <h3
      className={
        "mb-2 text-[11px] font-bold uppercase tracking-wide text-[#8A90A2]"
      }
    >
      {title}
    </h3>
    {items.length === 0 ? (
      <p className={"text-[13px] text-[#8A90A2]"}>{empty}</p>
    ) : (
      <ul className={"flex flex-col gap-3"}>
        {items.map((item) => {
          const action = onEmail ? waitingAction(item) : null;
          return (
            <li key={`${item.kind}:${item.id}`} className={"min-w-0"}>
              <ItemTitle item={item} onOpen={onOpen} />
              {item.kind === "work" && item.subtitle && (
                <div className={"truncate text-[12px] text-[#8A90A2]"}>
                  {item.subtitle}
                </div>
              )}
              <div
                className={"mt-0.5 text-[12.5px] leading-snug text-[#4A5065]"}
              >
                {item.reason}
              </div>
              {action && action.kind !== "none" && (
                <div className={"mt-1.5 flex flex-wrap gap-1.5"}>
                  {action.kind === "email" && (
                    <button
                      type={"button"}
                      className={LIGHT_BUTTON}
                      onClick={() => onEmail?.(item)}
                    >
                      Email
                    </button>
                  )}
                  {isAppLink(item.workLink) && (
                    <button
                      type={"button"}
                      className={LIGHT_BUTTON}
                      onClick={() => onOpen(item.workLink as string)}
                    >
                      Open work
                    </button>
                  )}
                  {action.kind === "client" && (
                    <button
                      type={"button"}
                      className={LIGHT_BUTTON}
                      onClick={() => onOpen(item.link)}
                    >
                      Open client
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    )}
    {footer && (
      <p
        className={
          "mt-3 border-t border-[#EDEFF3] pt-2.5 text-[12.5px] leading-snug text-[#4A5065]"
        }
      >
        {footer}
      </p>
    )}
  </section>
);

/** "Your day" on Home: a one-line band (collapsed, the default) or the full card. */
export const DailyBriefingView: FC<ViewProps> = ({
  briefing,
  band,
  writing = false,
  loading,
  failed,
  refreshing,
  expanded,
  onToggle,
  onRefresh,
  onRetry,
  onOpen,
  onEmail,
  onAsk,
  part,
}) => {
  const [question, setQuestion] = useState("");

  if (!expanded || !briefing) {
    if (part === "card") return null;
    const line = briefing ?? band;
    const chips = line ? countChips(line.counts) : [];
    return (
      <div
        className={classNames(
          BAND,
          "flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5",
          part === "band" ? "min-w-0 flex-1" : "mb-4",
        )}
        data-testid={"briefing-band"}
      >
        <span className={"text-[14px] font-extrabold"}>✨ Your day</span>
        {line &&
          (chips.length ? (
            chips.map((chip) => <Chip key={chip} text={chip} />)
          ) : (
            <span className={"text-[13px] text-[#6B7185]"}>
              Nothing urgent today.
            </span>
          ))}
        {expanded && writing && (
          <span className={"text-[13px] text-[#6B7185]"}>
            Writing your day…
          </span>
        )}
        {!line && loading && (
          <span className={"text-[13px] text-[#6B7185]"}>
            Getting your day ready…
          </span>
        )}
        {(expanded || !line) && !loading && !writing && failed && (
          <span className={"text-[13px] text-[#6B7185]"}>
            {"Couldn't put your day together right now."}
          </span>
        )}
        <span className={"ml-auto"} />
        {(expanded || !line) && !loading && !writing && failed ? (
          <button type={"button"} className={LIGHT_BUTTON} onClick={onRetry}>
            Try again
          </button>
        ) : (
          <button
            type={"button"}
            className={PURPLE_BUTTON}
            disabled={!line || writing}
            onClick={() => onToggle(true)}
          >
            Show my day
          </button>
        )}
      </div>
    );
  }

  if (part === "band") return null;

  const ask = (event: FormEvent) => {
    event.preventDefault();
    if (question.trim()) {
      onAsk(question.trim());
      setQuestion("");
    }
  };

  return (
    <section
      aria-label={"Your day"}
      className={classNames(BAND, "mb-4 p-4")}
      data-testid={"briefing-card"}
    >
      <div className={"flex flex-wrap items-start justify-between gap-3"}>
        <div className={"min-w-0"}>
          <h2 className={"text-[16px] font-extrabold"}>
            ✨ {briefingHeading(briefing.date)}
          </h2>
          <p className={"mt-0.5 text-[12px] text-[#8A90A2]"}>
            {writtenLine(briefing)}
          </p>
        </div>
        <div className={"flex shrink-0 items-center gap-1.5"}>
          <button
            type={"button"}
            className={LIGHT_BUTTON}
            onClick={onRefresh}
            disabled={refreshing}
          >
            <LuRefreshCw
              size={13}
              className={refreshing ? "animate-spin" : undefined}
            />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type={"button"}
            className={LIGHT_BUTTON}
            onClick={() => onToggle(false)}
          >
            <LuChevronUp size={14} />
            Collapse
          </button>
        </div>
      </div>

      {briefing.notice && (
        <p
          className={
            "mt-3 rounded-[9px] border border-[#F3D9A4] bg-[#FFF8E8] px-3 py-1.5 text-[12.5px] text-[#7A5410]"
          }
        >
          {briefing.notice}
        </p>
      )}
      {briefing.summary && (
        <p className={"mt-3 text-[14px] leading-relaxed text-[#1F2433]"}>
          {briefing.summary}
        </p>
      )}

      <div className={"mt-3 grid grid-cols-1 gap-3 md:grid-cols-3"}>
        <Column
          title={"Do first"}
          empty={"Nothing overdue or due in the next few days."}
          items={briefing.doFirst}
          onOpen={onOpen}
        />
        <Column
          title={"Waiting on clients"}
          empty={"No client is holding anything up."}
          items={briefing.waiting}
          onOpen={onOpen}
          onEmail={onEmail}
        />
        <Column
          title={"At risk this week"}
          empty={"Nothing looks at risk this week."}
          items={briefing.atRisk}
          onOpen={onOpen}
          footer={
            briefing.view === "firm" ? busiestLine(briefing.busiest) : null
          }
        />
      </div>

      <form onSubmit={ask} className={"mt-3 flex gap-2"}>
        <input
          type={"text"}
          value={question}
          maxLength={500}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={`Ask about your day, e.g. “${ASK_SUGGESTION}”`}
          aria-label={"Ask about your day"}
          className={
            "min-w-0 flex-1 rounded-[9px] border border-[#DCE2EA] bg-white px-3 py-1.5 text-[13px] text-[#1F2433] outline-none focus:border-[#6B4EFF]"
          }
        />
        <button
          type={"submit"}
          className={PURPLE_BUTTON}
          disabled={!question.trim()}
        >
          Ask
        </button>
      </form>
    </section>
  );
};

/**
 * Home's "Your day" briefing (owner 2026-10-31). Hidden when the firm's AI is
 * off or not set up. view "firm" = Firm view (Admins); otherwise the person's
 * own work. Collapsed by default; expanded is remembered for today only.
 */
/** The briefing's data and actions, shared by the band and the card (Home draws them in two places). */
export const useDailyBriefing = (view: "my" | "firm", userId?: string) => {
  const navigate = useNavigate();
  const { isOn, orgId } = useAiStatus();
  const [refreshes, setRefreshes] = useState<Record<string, number>>({});
  const [expanded, setExpanded] = useState(false);
  const { openCompose, composeModal } = useClientEmailCompose();

  useEffect(() => {
    if (userId) setExpanded(readExpanded(userId));
  }, [userId]);

  // Collapsed line: the plain lists, never an AI call (owner 2026-10-31: opening Home must not spend an AI request).
  const plain = useGetAiBriefingQuery(
    { orgId, view, ai: false },
    { skip: !isOn || !orgId },
  );
  // The AI wording: only once the person opens the card (then cached on the server for the day).
  const worded = useGetAiBriefingQuery(
    { orgId, view, refresh: refreshes[view] || 0 },
    { skip: !isOn || !orgId || !expanded },
  );

  // A sentence from the server (AI turned off meanwhile): hide quietly.
  const error = plain.error as { status?: number } | undefined;
  const hidden =
    !isOn ||
    !orgId ||
    (!plain.data && (error?.status === 400 || error?.status === 403));

  const toggle = (next: boolean) => {
    setExpanded(next);
    if (userId) saveExpanded(userId, next);
  };
  const card = expanded ? worded.data : undefined;

  const viewProps: ViewProps = {
    briefing: card,
    band: worded.data ?? plain.data,
    writing: expanded && !card && worded.isFetching,
    loading: plain.isLoading || plain.isFetching,
    failed: expanded ? !!worded.error : !!plain.error,
    refreshing: worded.isFetching && !!card,
    expanded,
    onToggle: toggle,
    onRefresh: () =>
      setRefreshes((current) => ({
        ...current,
        [view]: (current[view] || 0) + 1,
      })),
    onRetry: () => (expanded ? worded.refetch() : plain.refetch()),
    onOpen: (link) => navigate(link),
    onEmail: (item) =>
      openCompose({
        email: item.email,
        customerId: item.customerId,
        name: item.title,
        businessContactId: item.businessContactId,
      }),
    onAsk: (question) => openAskPanel(question),
  };
  return { hidden, viewProps, composeModal };
};

/** "Your day" in one place (band, or the card when opened). Home uses useDailyBriefing to split it. */
export const DailyBriefing: FC<{ view: "my" | "firm"; userId?: string }> = ({
  view,
  userId,
}) => {
  const { hidden, viewProps, composeModal } = useDailyBriefing(view, userId);
  if (hidden) return null;
  return (
    <>
      <DailyBriefingView {...viewProps} />
      {composeModal}
    </>
  );
};
